import {
  FILTER_TEMPLATE_ITEMS,
  filterByDraftTemplates,
  filterByPersonalTemplates,
  filterByTeamTemplates,
  getFilteredTemplatesByType,
  TemplateCardRowPreview,
} from '../../../entities/templates';
import { templatesModel } from '../../../features/templates';
import { contains } from '../../../utils/string';
import { FC, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SearchModalEmpty } from './SearchModalEmpty';
import { SearchModalNotFound } from './SearchModalNotFound';
import { TemplateExtended } from '@distribute/shared/api-types/templates';
import { SearchModalLabel } from './SearchModalLabel';

import { navigate } from '../../../processes/navigation';

import { SearchModalContentItem } from './SearchModalContentItem';
import {
  redirectActions,
  TEMPLATES_PERSONAL_ROUTE,
  TEMPLATES_TEAM_ROUTE,
} from '../../../entities/history';

import { usePreviewTemplate } from '../../../features/templates/hooks';
import { TemplateSharingAccess } from '@distribute/shared/types';
import { SearchModalTagFilters } from './SearchModalTagFilters';

type Props = {
  search: string;
  onClear: () => void;
  onClose: () => void;
};

export const SearchModalTemplates: FC<Props> = ({
  search,
  onClear,
  onClose,
}) => {
  const [filter, setFilter] = useState(
    Object.keys(FILTER_TEMPLATE_ITEMS)[0] as keyof typeof FILTER_TEMPLATE_ITEMS
  );

  const templates = useSelector(templatesModel.selectors.selectTemplates);

  const filteredTemplates = useMemo(() => {
    const _templates = templates.filter((template) =>
      contains(template.name || 'Untitled', search)
    );

    return getFilteredTemplatesByType(_templates, filter);
  }, [templates, search, filter]);

  const { personal, team, drafts } = useMemo(
    () => ({
      personal: filterByPersonalTemplates(filteredTemplates),
      team: filterByTeamTemplates(filteredTemplates),
      drafts: filterByDraftTemplates(filteredTemplates),
    }),
    [filteredTemplates]
  );

  if (search.length === 0) {
    return <SearchModalEmpty />;
  }

  if (filteredTemplates.length === 0) {
    return (
      <>
        <SearchModalTagFilters
          filter={filter}
          onFilterChange={setFilter}
          items={FILTER_TEMPLATE_ITEMS}
        />
        <SearchModalNotFound
          onClear={onClear}
          name="templates"
          className="h-[calc(100%-36px)]"
        />
      </>
    );
  }

  return (
    <>
      <SearchModalTagFilters
        filter={filter}
        onFilterChange={setFilter}
        items={FILTER_TEMPLATE_ITEMS}
      />
      <Content
        personal={personal}
        team={team}
        drafts={drafts}
        onClose={onClose}
      />
    </>
  );
};

const Content = ({
  personal,
  team,
  drafts,
  onClose,
}: {
  personal: TemplateExtended[];
  team: TemplateExtended[];
  drafts: TemplateExtended[];
  onClose: () => void;
}) => {
  const { handleTemplatePreview } = usePreviewTemplate();

  const dispatch = useDispatch();

  const handleOpenPreview = (template: TemplateExtended) => {
    dispatch(
      navigate({
        to:
          template.sharingAccess === TemplateSharingAccess.TEAM
            ? TEMPLATES_TEAM_ROUTE
            : TEMPLATES_PERSONAL_ROUTE,
      })
    );

    handleTemplatePreview(template);
    onClose();
  };

  const handleEdit = (template: TemplateExtended) => {
    dispatch(
      redirectActions.toTemplateEditorPage({
        sequenceNumber: template.id.toString(),
      })
    );
    onClose();
  };

  return (
    <div className="flex flex-col gap-y-4 mt-4">
      {team.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>Team</SearchModalLabel>
          {team.map((template) => (
            <ContentItem
              key={template.id}
              template={template}
              onClick={() => handleOpenPreview(template)}
            />
          ))}
        </div>
      )}
      {personal.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>Personal</SearchModalLabel>
          {personal.map((template) => (
            <ContentItem
              key={template.id}
              template={template}
              onClick={() => handleOpenPreview(template)}
            />
          ))}
        </div>
      )}
      {drafts.length > 0 && (
        <div className="flex flex-col gap-y-2">
          <SearchModalLabel>Draft</SearchModalLabel>
          {drafts.map((template) => (
            <ContentItem
              key={template.id}
              template={template}
              onClick={() => handleEdit(template)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContentItem = ({
  template,
  onClick,
}: {
  template: TemplateExtended;
  onClick: () => void;
}) => {
  return (
    <SearchModalContentItem onClick={onClick}>
      <TemplateCardRowPreview template={template} />
    </SearchModalContentItem>
  );
};
